import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiDownload, FiFileText, FiPaperclip, FiSearch, FiX } from 'react-icons/fi';
import {
  clientPortalService,
  type ClientPortalContext,
  type ClientPortalReport,
  type ClientPortalScope,
} from '../../services/clientPortalService';
import type { Report, ReportMedia } from '../../services/reportService';
import {
  buildReportSections,
  getReportPreview,
  getReportTitle,
  reportTypeLabelFor,
} from '../../utils/reportPresentation';
import { exportReportToDocx, exportReportToPdf } from '../../utils/reportExport';
import { useI18n } from '../../i18n';
import { formatDate, formatDateTime } from './clientPortalFormat';
import './ClientPortal.css';

interface Props {
  context: ClientPortalContext;
  siteId: string | null;
  can: (scope: ClientPortalScope) => boolean;
}

interface FeedDefinition {
  key: string;
  label: string;
  scope: ClientPortalScope;
  reportTypes: string[];
}

/**
 * The four client-facing feeds. `reportTypes` lists every raw value the guard
 * app has written for that feed, since older rows use looser spellings.
 */
const FEEDS: FeedDefinition[] = [
  {
    key: 'incidents',
    label: 'Incident Reports',
    scope: 'incidents',
    reportTypes: ['incident', 'incident_report', 'use_of_force', 'use_of_force_report', 'alarm', 'alarm_report'],
  },
  {
    key: 'daily',
    label: 'Daily Activity',
    scope: 'daily_reports',
    reportTypes: ['daily_activity', 'daily_activity_report', 'daily', 'daily_report', 'shift_summary', 'general', 'maintenance'],
  },
  {
    key: 'visitors',
    label: 'Visitor & Vehicle Logs',
    scope: 'visitor_logs',
    reportTypes: ['access_visitor', 'access', 'visitor', 'visitors', 'visitor_log', 'visitor_logs', 'access_log', 'access_visitor_log'],
  },
  {
    key: 'patrol',
    label: 'Patrol Reports',
    scope: 'patrol_records',
    reportTypes: ['patrol', 'patrol_report'],
  },
];

const PRIORITY_TONE: Record<string, string> = {
  critical: 'critical',
  high: 'high',
  medium: 'medium',
  low: 'low',
};

export default function ClientReportsPage({ context, siteId, can }: Props) {
  const { t } = useI18n();
  const availableFeeds = useMemo(() => FEEDS.filter((feed) => can(feed.scope)), [can]);
  const [activeFeed, setActiveFeed] = useState<string>(() => availableFeeds[0]?.key ?? 'incidents');
  const [reports, setReports] = useState<ClientPortalReport[]>([]);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selected, setSelected] = useState<ClientPortalReport | null>(null);
  const [media, setMedia] = useState<ReportMedia[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (availableFeeds.length > 0 && !availableFeeds.some((feed) => feed.key === activeFeed)) {
      setActiveFeed(availableFeeds[0].key);
    }
  }, [availableFeeds, activeFeed]);

  const feed = availableFeeds.find((item) => item.key === activeFeed) ?? availableFeeds[0] ?? null;

  const load = useCallback(async () => {
    if (!feed) {
      setReports([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const rows = await clientPortalService.fetchReports({
        reportTypes: feed.reportTypes,
        siteId,
        search,
        fromDate: fromDate ? new Date(`${fromDate}T00:00:00`).toISOString() : null,
        toDate: toDate ? new Date(`${toDate}T23:59:59`).toISOString() : null,
      });
      setReports(rows);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [feed, siteId, search, fromDate, toDate]);

  useEffect(() => {
    const timer = window.setTimeout(() => { void load(); }, search ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [load, search]);

  const openReport = async (report: ClientPortalReport) => {
    setSelected(report);
    setMedia([]);
    try {
      const rows = await clientPortalService.fetchReportMedia(report.id);
      setMedia(rows as ReportMedia[]);
    } catch {
      // A missing attachment must not block reading the report itself.
      setMedia([]);
    }
  };

  const handleExport = async (format: 'pdf' | 'docx') => {
    if (!selected) return;
    setIsExporting(true);
    try {
      const report = selected as unknown as Report;
      if (format === 'pdf') {
        await exportReportToPdf(report, [], media, false, context.company.name);
      } else {
        await exportReportToDocx(report, [], media, false, context.company.name);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsExporting(false);
    }
  };

  if (availableFeeds.length === 0) {
    return (
      <div className="cp-page">
        <div className="cp-notice">
          <FiFileText aria-hidden="true" />
          <div>
            <strong>{t('No report feeds are enabled')}</strong>
            <p>{t('Your security provider controls which reports reach this portal.')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-page">
      {error ? <div className="cp-banner cp-banner--error">{error}</div> : null}

      <div className="cp-tabs" role="tablist">
        {availableFeeds.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={item.key === activeFeed}
            className={`cp-tab${item.key === activeFeed ? ' cp-tab--active' : ''}`}
            onClick={() => { setActiveFeed(item.key); setSelected(null); }}
          >
            {t(item.label)}
          </button>
        ))}
      </div>

      <div className="cp-filters">
        <label className="cp-search">
          <FiSearch aria-hidden="true" />
          <input
            type="search"
            className="form__input"
            placeholder={t('Search reports')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <label className="cp-field">
          <span>{t('From')}</span>
          <input
            type="date"
            className="form__input"
            value={fromDate}
            onChange={(event) => setFromDate(event.target.value)}
          />
        </label>
        <label className="cp-field">
          <span>{t('To')}</span>
          <input
            type="date"
            className="form__input"
            value={toDate}
            onChange={(event) => setToDate(event.target.value)}
          />
        </label>
        {context.settings.report_history_days ? (
          <span className="cp-toolbar__note">
            {t('History limited to {count} days', { count: context.settings.report_history_days })}
          </span>
        ) : null}
      </div>

      <div className={`cp-split${selected ? ' cp-split--open' : ''}`}>
        <div className="cp-panel cp-panel--flush">
          {isLoading ? (
            <div className="cp-loading"><div className="spinner" /></div>
          ) : reports.length === 0 ? (
            <div className="cp-empty">{t('No reports match this view.')}</div>
          ) : (
            <ul className="cp-report-list">
              {reports.map((report) => {
                const tone = PRIORITY_TONE[(report.priority ?? '').toLowerCase()];
                return (
                  <li key={report.id}>
                    <button
                      type="button"
                      className={`cp-report-row${selected?.id === report.id ? ' cp-report-row--active' : ''}`}
                      onClick={() => void openReport(report)}
                    >
                      <div className="cp-report-row__main">
                        <strong>{getReportTitle(report)}</strong>
                        <p>{getReportPreview(report)}</p>
                      </div>
                      <div className="cp-report-row__meta">
                        {tone ? (
                          <span className={`badge badge--${tone}`}>{t(report.priority ?? '')}</span>
                        ) : null}
                        <span>{report.sites?.name ?? t('Unknown site')}</span>
                        <span>{formatDateTime(report.created_at)}</span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {selected ? (
          <aside className="cp-detail" aria-label={t('Report detail')}>
            <header className="cp-detail__head">
              <div>
                <div className="cp-eyebrow">{reportTypeLabelFor(selected.report_type)}</div>
                <h2>{getReportTitle(selected)}</h2>
                <p>
                  {selected.sites?.name ?? t('Unknown site')} · {formatDateTime(selected.created_at)}
                </p>
              </div>
              <button
                type="button"
                className="cp-icon-btn"
                aria-label={t('Close report')}
                onClick={() => setSelected(null)}
              >
                <FiX aria-hidden="true" />
              </button>
            </header>

            {can('exports') ? (
              <div className="cp-detail__actions">
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  disabled={isExporting}
                  onClick={() => void handleExport('pdf')}
                >
                  <FiDownload aria-hidden="true" /> {t('Download PDF')}
                </button>
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  disabled={isExporting}
                  onClick={() => void handleExport('docx')}
                >
                  <FiDownload aria-hidden="true" /> {t('Download Word')}
                </button>
              </div>
            ) : null}

            <div className="cp-detail__body">
              {buildReportSections(selected).map((sectionItem) => (
                <section key={sectionItem.title} className="cp-detail__section">
                  <h3>{sectionItem.title}</h3>
                  {sectionItem.facts.length > 0 ? (
                    <dl className="cp-facts">
                      {sectionItem.facts.map((fact) => (
                        <div key={`${sectionItem.title}-${fact.label}`}>
                          <dt>{fact.label}</dt>
                          <dd>{fact.value}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}
                  {sectionItem.body ? <p className="cp-detail__text">{sectionItem.body}</p> : null}
                </section>
              ))}

              {media.length > 0 ? (
                <section className="cp-detail__section">
                  <h3>{t('Attachments')}</h3>
                  <div className="cp-media">
                    {media.map((item) => (
                      item.url && item.media_type?.startsWith('image') ? (
                        <a key={item.id} href={item.url} target="_blank" rel="noreferrer">
                          <img src={item.url} alt={t('Report attachment')} />
                        </a>
                      ) : (
                        <a
                          key={item.id}
                          className="cp-media__file"
                          href={item.url ?? '#'}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <FiPaperclip aria-hidden="true" /> {item.media_type ?? t('Attachment')}
                        </a>
                      )
                    ))}
                  </div>
                </section>
              ) : null}

              <footer className="cp-detail__footer">
                {t('Released to your portal on {date}', {
                  date: formatDate(selected.client_released_at ?? selected.created_at),
                })}
              </footer>
            </div>
          </aside>
        ) : null}
      </div>
    </div>
  );
}
